import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Checkbox, ConfirmationModal, Typography } from 'seagull';
import { useOverlayContext, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { errorTextsPath } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
const unmarkPaidExternallyLoader = (invoiceId)=>"unmarkPaidExternally-".concat(invoiceId);
export async function executeUnmark(param) {
    let { invoice, suppressEmails, unmarkAsPaidExternally, startLoader, stopLoader, showErrorToast, showSuccessToast, successText, errorTexts } = param;
    const loaderName = unmarkPaidExternallyLoader(invoice.id);
    startLoader(loaderName);
    const notificationBehavior = suppressEmails ? 'silent' : 'resend';
    const result = await handleRequest(unmarkAsPaidExternally(invoice.id, {
        notificationBehavior
    }));
    if (result.success) {
        showSuccessToast(successText, 'fpi-unmark-paid-externally-success');
    } else {
        showErrorToast(result.error.unmarkPaidExternallyErrorText(invoice, errorTexts), 'fpi-unmark-paid-externally-error');
        stopLoader(loaderName);
    }
}
function UnmarkPaidExternallyModalContent(param) {
    let { contentText, suppressEmailsLabel, suppressEmails, onSuppressEmailsChange } = param;
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            display: 'flex',
            flexDirection: 'column',
            gap: 16
        },
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph1",
                children: contentText
            }),
            /*#__PURE__*/ _jsx(Checkbox, {
                bi: {
                    name: 'unmark_paid_externally_suppress_emails'
                },
                checked: suppressEmails,
                onChange: (e)=>onSuppressEmailsChange(e.target.checked),
                testId: "unmark-paid-externally-suppress-emails",
                label: suppressEmailsLabel
            })
        ]
    });
}
function UnmarkPaidExternallyModal(param) {
    let { dismissModal, onConfirm } = param;
    const { t: unmarkPaidExternallyTexts } = useLocaleKeys('hooks.unmarkPaidExternally');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const [suppressEmails, setSuppressEmails] = useState(false);
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'unmark_paid_externally_confirm'
            },
            secondaryButton: {
                name: 'unmark_paid_externally_cancel'
            }
        },
        title: unmarkPaidExternallyTexts.title(),
        content: /*#__PURE__*/ _jsx(UnmarkPaidExternallyModalContent, {
            contentText: unmarkPaidExternallyTexts.content(),
            suppressEmailsLabel: unmarkPaidExternallyTexts.suppressEmailsLabel(),
            suppressEmails: suppressEmails,
            onSuppressEmailsChange: setSuppressEmails
        }),
        testId: "unmark-paid-externally-confirmation-modal",
        textAlignCenter: false,
        primaryButtonLabel: unmarkPaidExternallyTexts.unmarkAsPaid(),
        secondaryButtonLabel: commonButtons.cancel(),
        onSecondaryButtonClick: ()=>dismissModal(false),
        onCloseButtonClick: ()=>dismissModal(false),
        onPrimaryButtonClick: ()=>onConfirm(suppressEmails)
    });
}
export function useUnmarkPaidExternally() {
    const { api } = useStoreActions();
    const { startLoader, stopLoader, showErrorToast, showSuccessToast, showModal } = useOverlayContext();
    const { t: unmarkPaidExternallyTexts } = useLocaleKeys('hooks.unmarkPaidExternally');
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    return {
        async startUnmarkPaidExternally (invoice) {
            if (invoice.isAdHoc) {
                showModal({
                    biName: 'unmark_paid_externally_confirm',
                    closeOnClickOutside: true,
                    closeOnEscapeKey: true,
                    openBottomSheetOnMobile: true,
                    hideBottomSheetOnDismiss: true,
                    content: (param)=>{
                        let { dismissModal } = param;
                        return /*#__PURE__*/ _jsx(UnmarkPaidExternallyModal, {
                            dismissModal: dismissModal,
                            onConfirm: (suppressEmails)=>{
                                dismissModal(true);
                                executeUnmark({
                                    invoice,
                                    suppressEmails,
                                    unmarkAsPaidExternally: api.invoice.unmarkAsPaidExternally,
                                    startLoader,
                                    stopLoader,
                                    showErrorToast,
                                    showSuccessToast,
                                    successText: suppressEmails ? unmarkPaidExternallyTexts.successNoEmails() : unmarkPaidExternallyTexts.successEmailsSent(),
                                    errorTexts
                                });
                            }
                        });
                    }
                });
            } else {
                startLoader(unmarkPaidExternallyLoader(invoice.id));
                const result = await handleRequest(api.invoice.unmarkAsPaidExternally(invoice.id));
                if (result.success) {
                // Pusher event stops the loader and updates the invoice
                } else {
                    showErrorToast(result.error.unmarkPaidExternallyErrorText(invoice, errorTexts), 'fpi-unmark-paid-externally-error');
                    stopLoader(unmarkPaidExternallyLoader(invoice.id));
                }
            }
        },
        async finishUnmarkPaidExternally (invoiceId) {
            stopLoader(unmarkPaidExternallyLoader(invoiceId));
        }
    };
}
